/**
 * Rollforward Analysis Screen
 *
 * Inspects the computed rollforward and surfaces every day that does NOT roll
 * (Discrepancy above tolerance), explaining *why*: the roll books activity by
 * Issue_Date, but the outstanding balance also moves when items close
 * (Close_Date), and those closures aren't in the issue-date flow. So each
 * breaking day is explained by the records that closed on that day — shown
 * grouped by document type, with the largest individual records and an
 * "explained %" of how much of the gap the closures account for.
 *
 * Read-on-demand: reads the rollforward output, so it's gated on hasRollforward.
 */
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { analyzeRollforward } from "Redux/data/actions";

const money = (n) => {
  if (n === "" || n === null || n === undefined || isNaN(n)) return "—";
  const abs = Math.abs(n);
  const s =
    abs >= 1e6 ? `${(n / 1e6).toFixed(2)}M` :
    abs >= 1e3 ? `${(n / 1e3).toFixed(1)}K` :
    n.toFixed(2);
  return s;
};

// -------------------------------------------------------------
const RollforwardAnalysis = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { rollforwardAnalysis: analysis } = useSelector((state) => state.data);
  const { hasRollforward } = useSelector((state) => state.project);
  const dispatch = useDispatch();

  const run = () => {
    setIsLoading(true);
    dispatch(analyzeRollforward(() => setIsLoading(false)));
  };

  if (!hasRollforward)
    return (
      <div className="notification is-warning is-light">
        Rollforward hasn't been computed yet. Run the rollforward first, then analyse it.
      </div>
    );

  const summary = analysis?.summary;
  const days = analysis?.days || [];

  return (
    <div>
      {/* Header + run button */}
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--ev-text)" }}>
            Rollforward Analysis
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--ev-text-muted)" }}>
            Finds every day the roll doesn't reconcile and explains the gap by the records that closed that day.
          </div>
        </div>
        <button
          className={`button is-small ${isLoading ? "is-loading" : ""}`}
          onClick={run}
          disabled={isLoading}
          style={{ backgroundColor: "#79589F", color: "#fff", border: "none", fontWeight: 700, borderRadius: "8px" }}
        >
          <i className="fa-duotone fa-magnifying-glass-chart" style={{ marginRight: "0.4rem" }} />
          {analysis ? "Re-analyse" : "Analyse Rollforward"}
        </button>
      </div>

      {isLoading && (
        <div style={{ fontSize: "0.85rem", color: "var(--ev-text-muted)", fontStyle: "italic", padding: "1rem 0" }}>
          Analysing rollforward reconciliation...
        </div>
      )}

      {!isLoading && !analysis && (
        <div className="notification is-info is-light" style={{ fontSize: "0.82rem" }}>
          Run the analysis to locate every day where the rollforward doesn't reconcile and see what caused it.
        </div>
      )}

      {!isLoading && summary && (
        <>
          {/* Summary tiles */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.25rem" }}>
            <Tile label="Days reconcile" value={`${summary.rolling_days.toLocaleString()} / ${summary.total_days.toLocaleString()}`} sub={`${summary.pct_rolling}%`} tone={summary.breaking_days === 0 ? "success" : "info"} />
            <Tile label="Days breaking" value={summary.breaking_days.toLocaleString()} tone={summary.breaking_days === 0 ? "success" : "danger"} />
            <Tile label="Max discrepancy" value={money(summary.max_discrepancy)} tone="warning" />
            <Tile label="Total discrepancy" value={money(summary.total_discrepancy)} tone="warning" />
          </div>

          {summary.breaking_days === 0 ? (
            <div className="notification is-success is-light" style={{ fontSize: "0.82rem" }}>
              Every day reconciles within tolerance ({summary.tolerance}). The rollforward is internally consistent.
            </div>
          ) : (
            <>
              <div style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", marginBottom: "0.5rem" }}>
                Showing {days.length.toLocaleString()} of {summary.breaking_days.toLocaleString()} breaking days (largest first). Click a row to see the closing records that caused it.
              </div>

              {/* Breaking-day list */}
              <table className="table is-fullwidth is-striped is-bordered is-small-table">
                <thead>
                  <tr>
                    <th style={{ fontSize: "0.7rem" }}></th>
                    <th style={{ fontSize: "0.7rem" }}>Date</th>
                    <th style={{ fontSize: "0.7rem", textAlign: "right" }}>Discrepancy</th>
                    <th style={{ fontSize: "0.7rem", textAlign: "right" }}>Explained</th>
                    <th style={{ fontSize: "0.7rem", textAlign: "right" }}>Beginning</th>
                    <th style={{ fontSize: "0.7rem", textAlign: "right" }}>Ending</th>
                    <th style={{ fontSize: "0.7rem", textAlign: "right" }}>Ending Check</th>
                    <th style={{ fontSize: "0.7rem", textAlign: "right" }}># Closed</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d, i) => {
                    const open = expanded === i;
                    const full = d.explained_pct >= 99.5;
                    return (
                      <>
                        <tr key={i} onClick={() => setExpanded(open ? null : i)} style={{ cursor: "pointer" }}>
                          <td style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)" }}>
                            <i className={`fa-solid ${open ? "fa-chevron-down" : "fa-chevron-right"}`} />
                          </td>
                          <td style={{ fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap" }}>{d.date}</td>
                          <td style={{ fontSize: "0.72rem", textAlign: "right", fontWeight: 700, color: "var(--ev-danger)" }}>{money(d.discrepancy)}</td>
                          <td style={{ fontSize: "0.72rem", textAlign: "right" }}>
                            <span className={`tag is-small ${full ? "is-success" : "is-warning"} is-light`} style={{ borderRadius: "4px" }}>
                              {d.explained_pct}%
                            </span>
                          </td>
                          <td style={{ fontSize: "0.72rem", textAlign: "right", whiteSpace: "nowrap" }}>{money(d.beginning)}</td>
                          <td style={{ fontSize: "0.72rem", textAlign: "right", whiteSpace: "nowrap" }}>{money(d.ending)}</td>
                          <td style={{ fontSize: "0.72rem", textAlign: "right", whiteSpace: "nowrap" }}>{money(d.ending_check)}</td>
                          <td style={{ fontSize: "0.72rem", textAlign: "right" }}>{d.n_closed}</td>
                        </tr>
                        {open && (
                          <tr key={`${i}-detail`}>
                            <td colSpan={8} style={{ backgroundColor: "var(--ev-surface)", padding: "0.75rem 1rem" }}>
                              <DayDetail d={d} />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
};

// -------------------------------------------------------------
const DayDetail = ({ d }) => (
  <div>
    <div style={{ fontSize: "0.78rem", color: "var(--ev-text)", marginBottom: "0.6rem" }}>
      <strong>Why it doesn't roll:</strong> {d.n_closed.toLocaleString()} record{d.n_closed === 1 ? "" : "s"} closed on {d.date},
      moving the balance by <strong>{money(d.closed_amount)}</strong> without a matching issue-date activity entry.
      That accounts for <strong>{d.explained_pct}%</strong> of the {money(d.discrepancy)} discrepancy
      {d.residual > 0.01 ? <> — a residual of <strong>{money(d.residual)}</strong> remains (check for a document type mapped to an unrecognised category).</> : "."}
    </div>

    {d.by_type.length > 0 && (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
        {d.by_type.map((t) => (
          <span key={t.type} className="tag is-info is-light" style={{ borderRadius: "6px", fontSize: "0.7rem", fontWeight: 600 }}>
            {t.type}: {money(t.amount)}
            <span className="tag is-dark is-small" style={{ marginLeft: "0.4rem", borderRadius: "4px" }}>{t.count}</span>
          </span>
        ))}
      </div>
    )}

    {d.records.length > 0 && (
      <div className="table-container">
        <table className="table is-fullwidth is-narrow is-small-table" style={{ fontSize: "0.7rem" }}>
          <thead>
            <tr>
              <th>Type</th><th>Document No.</th><th>Buyer</th><th>Issued</th><th>Closed</th><th style={{ textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {d.records.map((r, j) => (
              <tr key={j}>
                <td>{r.Document_Type}</td>
                <td style={{ whiteSpace: "nowrap" }}>{r.Document_Number}</td>
                <td>{r.Buyer}</td>
                <td style={{ whiteSpace: "nowrap" }}>{r.Issue_Date}</td>
                <td style={{ whiteSpace: "nowrap" }}>{r.Close_Date}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{money(r.Amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// -------------------------------------------------------------
const TONES = {
  success: "var(--ev-success)", danger: "var(--ev-danger)",
  warning: "var(--ev-warning)", info: "var(--ev-purple)",
};
const Tile = ({ label, value, sub, tone = "info" }) => (
  <div style={{
    flex: "1 1 140px", minWidth: "130px", padding: "0.6rem 0.8rem",
    border: "1px solid var(--ev-border)", borderRadius: "8px",
    borderLeft: `3px solid ${TONES[tone]}`, backgroundColor: "var(--ev-white)",
  }}>
    <div style={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--ev-text-muted)" }}>{label}</div>
    <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--ev-text)" }}>{value}</div>
    {sub && <div style={{ fontSize: "0.7rem", color: "var(--ev-text-muted)" }}>{sub}</div>}
  </div>
);

export default RollforwardAnalysis;
